"use client";

import {
  cn,
  Icon,
  TokenIcon,
  type TokenIconSymbol,
  Tooltip,
  Typography,
} from "@kyan/ui";

import { LoadOrRender } from "#/components/LoadOrRender/LoadOrRender";
import { useHedgeSummaryController } from "#/hooks/useHedgeSummary.controller";
import { formatCurrency } from "#/utils/valueFormatters";

export const HedgeSummary: React.FC = () => {
  const {
    isLoading,
    lastTrade,
    priceDetails,
    hedgeDetails,
    optionUnderlying,
    handleRemoveHedge,
  } = useHedgeSummaryController();

  return (
    <div className="bg-buttonBg dark:bg-textSecondary/15 mt-2 flex h-21 w-full flex-col rounded-md p-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-textSecondary/10 flex rounded-sm">
            <TokenIcon
              size="sm"
              className="text-textPrimary h-7 w-10"
              symbol={optionUnderlying.toLowerCase() as TokenIconSymbol}
            />

            <div className="border-divider border-r" />

            <Typography
              grotesk
              primary
              size={12}
              className={cn(
                "flex h-7 w-10 items-center justify-center capitalize",
                {
                  "text-success": hedgeDetails?.hedgeIsBuy,
                  "text-error": !hedgeDetails?.hedgeIsBuy,
                },
              )}
            >
              {hedgeDetails?.hedgeDirection}
            </Typography>
          </div>

          <div className="grid h-7 items-center gap-0.5 -space-y-1">
            <Typography primary>{`${optionUnderlying} Perpetual`}</Typography>

            <LoadOrRender isLoading={isLoading}>
              {Boolean(lastTrade?.price) && (
                <Typography grotesk size={10}>
                  {`Last price ${formatCurrency(lastTrade.price)}`}
                </Typography>
              )}
            </LoadOrRender>
          </div>
        </div>

        <button
          type="button"
          className="text-textSecondary hover:text-textPrimary transition-color flex h-5 cursor-pointer items-center pr-1.5"
          onClick={handleRemoveHedge}
        >
          <Icon size="sm" name="cross" />
        </button>
      </div>

      <div className="mt-1.5 grid gap-2">
        {priceDetails.map((detail) => (
          <div key={detail.label} className="flex items-center justify-between">
            <Tooltip content={detail.tooltip} disabled={!detail.tooltip}>
              <div>
                <Typography
                  xs
                  grotesk
                  className={cn({
                    underline: detail.tooltip,
                  })}
                >
                  {detail.label}
                </Typography>
              </div>
            </Tooltip>
            <div className="flex items-center gap-1">
              <Typography xs grotesk primary>
                {detail.value}
              </Typography>
              <Typography xs grotesk>
                {detail.unit}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
